import React, { type FC, type MouseEvent, useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";

import { useSnackbar } from "notistack";

//import CryptoJS from "crypto-js";

import md5 from 'md5';

import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from "react-router-dom";

import { isFalsy } from "utility-types";
import { useLocalStorage } from "react-use";

import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { IconButton, InputAdornment, Typography } from "@mui/material";

import { Visibility, VisibilityOff } from "@mui/icons-material";

import {
    currentFormNameAtom,
    currentUserSessionAtom,
    hubConnectionIdAtom,
    isSaveLoadingAtom,
} from 'library/store';

import {
    type IPasswordChange,
    type IUserCredential,
    type IUserSession,
    defaultPasswordChange,
} from "features/identity/models/User";

import useIdentityUserService from "features/identity/services/User";
import { useRecoilState, useRecoilValue } from "recoil";

import type { IResult } from "library/interface";
import AuthCardWrapper from "./AuthCardWrapper";
import useVisibilityToggle from "library/useVisibilityToggle";

import useAxios from "library/axios";
//import useEmailService from "features/identity/services/Email";




const PwdForm = () => {
    const axios = useAxios();

    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const { isPasswordStrong } = useIdentityUserService();
    //const { sendOtpEmail } = useEmailService();

    const [currentUserSession, setCurrentUserSession] = useRecoilState(
        currentUserSessionAtom
    );
    // const { language, username, isPasswordComplexityAllowed } = useRecoilValue(currentUserSessionAtom);

    const [currentFormName, setCurrentFormNameAtom] =
        useRecoilState(currentFormNameAtom);
    const [isSaveLoading, setIsSaveLoading] = useRecoilState(isSaveLoadingAtom);

    const [storeTenant, setStoreTenant, removeStoreTenant] =
        useLocalStorage<string>("Tenant");
    const [isLoadingOtp, setIsLoadingOtp] = useState(false);

    // const formSchema = yup.object().shape({
    //   newPassword: yup.string()
    //     .required('Password is mendatory')
    //     .min(3, 'Password must be at 3 char long'),
    //     confirmNewPassword: yup.string()
    //     .required('Password is mendatory')
    //     .oneOf([yup.ref('password')], 'Passwords does not match'),
    // })

    const { show: showPassword, toggleVisibility: togglePasswordVisibility } =
        useVisibilityToggle();

    const methods = useForm<IUserCredential>({
        defaultValues: { ...currentUserSession, password: "" },
    });
    const {
        register,
        getValues,
        setValue,
        formState: { errors, isSubmitting },
    } = methods;

    const [hubConnectionId, setHubConnectionId] =
        useRecoilState(hubConnectionIdAtom);

    const onPrevious = () => {
        navigate(-1);
    };

    const postCredential = async (md5Credential: IUserCredential) =>
        await (
            await axios.post("/api/identity/token/get-token", { ...md5Credential })
        ).data;

    const { isError, isSuccess, error, mutate } = useMutation<
        IResult<IUserSession>,
        Error,
        IUserCredential
    >({
        mutationFn: postCredential,
        onSuccess: (data: IResult<IUserSession>) => {
            if (!data.succeeded) {
                enqueueSnackbar(data.messages.join("\n"), {
                    variant: "error",
                    anchorOrigin: { horizontal: "center", vertical: "top" },
                    autoHideDuration: 2000,
                });
                return;
            }

            if (!data.data.isAuthenticated) {
                enqueueSnackbar(t("Invalid user credential"), {
                    variant: "error",
                    anchorOrigin: { horizontal: "center", vertical: "top" },
                    autoHideDuration: 2000,
                });
                return;
            }

            var shouldResetPassword = data.data.shouldResetPassword;

            if (
                !data.data.shouldResetPassword &&
                data.data.displayPasswordExpirationWarning
            ) {
                enqueueSnackbar(
                    t(
                        "Your password is going to expire in few days, you have to change it."
                    ),
                    {
                        variant: "warning",
                        anchorOrigin: { horizontal: "center", vertical: "top" },
                        autoHideDuration: 4500,
                    }
                );
            }

            if (
                data.data.isPasswordComplexityAllowed &&
                !isPasswordStrong(getValues().password)
            ) {
                enqueueSnackbar(
                    t(
                        "You must change your password because the complexity is not respeted"
                    ),
                    {
                        variant: "error",
                        anchorOrigin: { horizontal: "center", vertical: "top" },
                        autoHideDuration: 2000,
                    }
                );
                //data.data.shouldResetPassword
                shouldResetPassword = true;
            }

            setCurrentUserSession({ ...data.data, isUsernameValid: true, /*startTime: new Date(),*/ shouldResetPassword });

            if (shouldResetPassword) navigate({ pathname: `/passwordChange` });
            else navigate('/home');
        },
        onError: (err: Error) => {
            enqueueSnackbar(error?.message, {
                variant: "error",
                anchorOrigin: { horizontal: "center", vertical: "top" },
                autoHideDuration: 2000,
            });
            setIsSaveLoading(false);
        },
    });

    const postPwd = async (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();

        const { userName, password, language } = getValues();

        const { hostname, host } = window.location;

        const tempTenant = window.location.search.replace("?", "");
        //const tenantAlias =  host.split('.')[0];

        const tenantAlias = isFalsy(tempTenant) ? storeTenant || "" : tempTenant;

        if (tenantAlias === "") {
            enqueueSnackbar(
                t("The url is invalid, you have to add ?xxxx at the of url"),
                {
                    variant: "error",
                    anchorOrigin: { horizontal: "center", vertical: "top" },
                    autoHideDuration: 2000,
                }
            );

            const tenant = "xxxx";
            window.location.replace(`${host}?${tenant}`);
            return;
        }

        setStoreTenant(tenantAlias);

        mutate({
            userName,
            password: md5(password),
            language,
            tenantAlias,
            hubConnectionId,
        });
    };

    const handleOtpRequest = async (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        navigate("/auth/otp");

        const { userName, language } = currentUserSession;

        // if (!username) {
        //   enqueueSnackbar(t("Email not found. Please start from the beginning."), {
        //     variant: "error",
        //     anchorOrigin: { horizontal: "center", vertical: "top" },
        //     autoHideDuration: 2000,
        //   });
        //   navigate("/auth");
        //   return;
        // }

        const tempTenant = window.location.search.replace("?", "");
        const tenantAlias = isFalsy(tempTenant) ? storeTenant || "" : tempTenant;

        if (tenantAlias === "") {
            enqueueSnackbar(
                t("The url is invalid, you have to add ?xxxx at the of url"),
                {
                    variant: "error",
                    anchorOrigin: { horizontal: "center", vertical: "top" },
                    autoHideDuration: 2000,
                }
            );
            return;
        }

        setIsLoadingOtp(true);
        try {
            // await sendOtpEmail({
            //     email: userName,
            //     tenantAlias,
            //     language,
            // });

            // Navigate to OTP form
            navigate("/auth/otp");
        } catch (error) {
            console.error("Error sending OTP:", error);
        } finally {
            setIsLoadingOtp(false);
        }
    };

    return (
        <AuthCardWrapper title={t("Password or OTP")}
            description={t("You can can either connect with password or request for OTP")}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                    width: "100%",
                    overflow: "hidden",
                    gap: 1,
                }}
            >
                <Button
                    variant="text"
                    onClick={onPrevious}
                    sx={{
                        textTransform: "none",
                        minWidth: "auto",
                        px: 0,
                        flexShrink: 0,
                    }}
                >
                    ← {t("Back")}
                </Button>

                <Typography
                    variant="body2"
                    noWrap
                    sx={{
                        color: "text.secondary",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {t("Signing in as")}{" "}
                    <strong>{getValues().userName}</strong>
                </Typography>
            </Box>
            <FormProvider {...methods}>
                <Box
                    sx={{
                        paddingY: 1,
                        width: "100%",
                    }}
                >
                    <TextField
                        type={showPassword ? "text" : "password"}
                        sx={{
                            width: "100%",
                        }}
                        error={!!errors.userName}
                        helperText={errors.userName?.message as string | undefined}
                        required
                        label={t("Password")}
                        {...register("password")}
                        inputProps={{ autoComplete: 'new-password', style: { textTransform: 'none' } }}
                        InputProps={{
                            onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
                                if (event.key === "Enter") {
                                    const btnPostCredential = document.getElementById("btnPostCredential");
                                    btnPostCredential?.click();
                                }
                            },
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={togglePasswordVisibility} edge="end">
                                        {showPassword ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}

                    />
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Button
                        id="btnPostCredential"
                        variant="contained"
                        onClick={postPwd}
                        disabled={isSubmitting}
                        //loading={isSubmitting}
                        sx={{
                            width: "100%",
                            padding: 1.5,
                            mt: 2,
                        }}
                    >
                        {t("Signin")}
                    </Button>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            my: 2,
                        }}
                    >
                        <Box sx={{ flex: 1, height: 1, bgcolor: "divider" }} />
                        <Typography sx={{ px: 2, color: "text.secondary" }}>
                            {t("or")}
                        </Typography>
                        <Box sx={{ flex: 1, height: 1, bgcolor: "divider" }} />
                    </Box>

                    <Button
                        variant="outlined"
                        onClick={handleOtpRequest}
                        disabled={isLoadingOtp || true}
                        //loading={isLoadingOtp}
                        
                        sx={{
                            width: "100%",
                            padding: 1.5,
                        }}
                    >
                        {t("Sign in with OTP")}
                    </Button>
                    <Box
                        sx={{
                            marginY: 1.3,
                        }}
                    >
                        {t("forgotPassword")}{" "}
                        <Typography
                            component="a"
                            href="/auth/forgot-password"
                            sx={{
                                color: (theme) => theme.palette.primary.main,
                            }}
                        >
                            {t("recoverLink")}
                        </Typography>
                    </Box>
                </Box>
            </FormProvider>
        </AuthCardWrapper>
    );
};

export default PwdForm;
